import { useCallback } from "react";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { Paginated, PaginationQuery } from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import { AdminClamping, UpsertAdminClampingBody } from "./types/patrolClampings";

export const useClampingsApi = () => {
  const api = useApi();

  const getAdminClamping = useCallback(
    (signal: AbortSignal) => {
      const url = `/admin/settings/patrol/system_clampings`;
      return useSWRMutation<AdminClamping, CustomError, string, string>(url, (url, { arg }: { arg: string }) =>
        api.get<AdminClamping>({ url: `${url}/${arg}`, signal }),
      );
    },
    [api],
  );

  const useAdminClampings = useCallback(
    (query: PaginationQuery, signal: AbortSignal, systemId?: string) => {
      const url = buildUrl(
        systemId ? `/admin/settings/patrol/systems/${systemId}/clampings` : `/admin/settings/patrol/system_clampings`,
        query,
      );
      return useSWR<Paginated<AdminClamping>, CustomError, string>(url, (url) =>
        api.get<Paginated<AdminClamping>>({ url, signal }),
      );
    },
    [api],
  );

  const insertAdminClamping = useCallback(
    async (body: UpsertAdminClampingBody): Promise<{ id: string }> => {
      const url = `/admin/settings/patrol/system_clampings`;
      const id = await api.post<{ id: string }, UpsertAdminClampingBody>(url, body);
      mutateCache([url, `/admin/settings/patrol/systems/${body.systemId}/clampings`]);
      return id;
    },
    [api],
  );

  const updateAdminClamping = useCallback(
    async (clampingId: string, body: UpsertAdminClampingBody): Promise<void> => {
      const url = `/admin/settings/patrol/system_clampings/${clampingId}`;
      (await api.put<UpsertAdminClampingBody>(url, body),
        mutateCache(["/admin/settings/patrol/clampings", `/admin/settings/patrol/systems/${body.systemId}/clampings`]));
    },
    [api],
  );

  const deleteAdminClamping = useCallback(
    async (systemId: string, clampingId: string): Promise<void> => {
      const url = `/admin/settings/patrol/system_clampings/${clampingId}`;
      await api.del(url);
      mutateCache([
        url,
        `/admin/settings/patrol/system_clampings`,
        `/admin/settings/patrol/systems/${systemId}/clampings`,
      ]);
    },
    [api],
  );

  return {
    getAdminClamping,
    useAdminClampings,
    insertAdminClamping,
    updateAdminClamping,
    deleteAdminClamping,
  };
};
